import { buildCostBreakdownItems } from '@client/components/pricing/SeasonalPricing/utils/estimateDisplay';
import { useIsCrossCategoryLinkEnabled } from '@client/hooks/use-cross-category-link';
import { estimateServiceBreakdownAtom } from '@client/jotai/storefront/pricing';
import { useResolvedLocation } from '@components/listings-search-bar/adapters/hooks/use-resolved-location';
import {
	buildCategoryLocationPath,
	codeToSlugNameMap,
} from '@components/listings-search-bar/adapters/utils';
import { NOT_OFFERED_LABEL } from '@constants/pricing';
import { TextBody } from '@tkww/orion-web-ds-text';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import type { PricingEstimationService } from '../../../types';
import { useTrackEstimateDisplayed } from '../../hooks/useTrackEstimateDisplayed';
import {
	CostBreakdownItem,
	type CostBreakdownItemCrossCategoryLink,
} from '../CostBreakdownItem';
import Styles from './styles.scss';

type CrossCategoryLinkConfig = {
	label: string;
	categoryCode: keyof typeof codeToSlugNameMap;
};

const CROSS_CATEGORY_LINKS: Partial<
	Record<PricingEstimationService, CrossCategoryLinkConfig>
> = {
	catering: {
		label: 'Browse caterers',
		categoryCode: 'CAT',
	},
	barServices: {
		label: 'Browse bar service',
		categoryCode: 'BAR',
	},
};

const buildDestinationUrl = ({
	categoryCode,
	city,
	stateCode,
}: Pick<CrossCategoryLinkConfig, 'categoryCode'> & {
	city: string | null;
	stateCode: string | null;
}) => {
	const slug = codeToSlugNameMap[categoryCode].href;
	if (!city || !stateCode) return `/marketplace/${slug}`;

	const path = buildCategoryLocationPath(categoryCode, city, stateCode);
	return path ? `/marketplace/${path}` : `/marketplace/${slug}`;
};

export const CostBreakdown = () => {
	const costBreakdown = useAtomValue(estimateServiceBreakdownAtom);
	const trackEstimateDisplayed = useTrackEstimateDisplayed();
	const isCrossCategoryLinkEnabled = useIsCrossCategoryLinkEnabled();
	const { city, stateCode } = useResolvedLocation();

	const items = useMemo(
		() => buildCostBreakdownItems(costBreakdown),
		[costBreakdown],
	);

	useEffect(() => {
		if (items.length === 0) return;

		trackEstimateDisplayed(
			Object.fromEntries(
				items.map(({ title, cost }) => [title, cost ?? NOT_OFFERED_LABEL]),
			),
		);
	}, [items, trackEstimateDisplayed]);

	if (items.length === 0) return null;

	const getCrossCategoryLink = (
		item: (typeof items)[number],
	): CostBreakdownItemCrossCategoryLink | undefined => {
		if (!isCrossCategoryLinkEnabled || !item.isUnavailable) return undefined;

		const config = CROSS_CATEGORY_LINKS[item.key];
		if (!config) return undefined;

		return {
			label: config.label,
			categoryCode: config.categoryCode,
			destinationUrl: buildDestinationUrl({
				categoryCode: config.categoryCode,
				city,
				stateCode,
			}),
		};
	};

	return (
		<div
			className={Styles.costBreakdownContainer}
			data-testid="cost-breakdown-container"
		>
			<div className={Styles.costBreakdownHeading}>
				<TextBody size="xl" bold>
					Price breakdown
				</TextBody>
				<TextBody size="md" color="subtle-dark">
					Service fees, taxes, gratuity and item rental fees not included.
				</TextBody>
			</div>
			<div className={Styles.costBreakdownContent} id="cost-breakdown-content">
				<div className={Styles.costBreakdownList}>
					{items.map((item) => (
						<CostBreakdownItem
							key={item.key}
							iconName={item.icon}
							title={item.title}
							cost={item.cost}
							isFree={item.isFree}
							isLocalAverage={item.isLocalAverage}
							isStartingPrice={item.isStartingPrice}
							isUnavailable={item.isUnavailable}
							crossCategoryLink={getCrossCategoryLink(item)}
						/>
					))}
				</div>
			</div>
		</div>
	);
};
